import { AbstractControl, ValidatorFn } from '@angular/forms';
import {
  APP_DATE_FORMAT,
  formatDateTime,
  getDateUtc,
  isDateValid,
} from '@wz-utilities/date.utilities';
import {
  format,
  isAfter,
  isBefore,
  isSameDay,
  subDays,
  subYears,
} from 'date-fns';
import { ResourcesService } from '../../tunnelsante/services/resources/resources.service';

export class WzDateValidators {
  private static resService: ResourcesService;

  static setResourcesService(resService: ResourcesService) {
    this.resService = resService;
  }

  static majorPerson(date?: Date): ValidatorFn {
    return this.compareToDate(
      subDays(subYears(date || getDateUtc() || new Date(), 18), 1),
      (a: any, b: any) => isSameDay(a, b) || isBefore(a, b),
      'majorPerson',
    );
  }

  static invalid(): ValidatorFn {
    return (c: AbstractControl) => {
      if (c.value && !isDateValid(getDateUtc(c.value))) {
        return { invalid: 'Bad format' };
      }

      return null;
    };
  }

  static min(date: Date): ValidatorFn {
    return this.compareToDate(
      date,
      (a: any, b: any) => isSameDay(a, b) || isAfter(a, b),
      'min',
    );
  }

  static max(date: Date): ValidatorFn {
    return this.compareToDate(
      date,
      (a: any, b: any) => isSameDay(a, b) || isBefore(a, b),
      'max',
    );
  }

  //validation pour les dates avec separateur '-', ex: 1978-01-05
  static minDate(date: Date, key: string = 'minDate'): ValidatorFn {
    return this.compareToDateFormated(
      date,
      (a: any, b: any) => isSameDay(a, b) || isAfter(a, b),
      key,
    );
  }

  //validation pour les dates avec separateur '-', ex: 1978-01-05
  static maxDate(date: Date, key: string = 'maxDate'): ValidatorFn {
    return this.compareToDateFormated(
      date,
      (a: any, b: any) => {
        return isSameDay(a, b) || isBefore(a, b);
      },
      key,
    );
  }

  static minControl(controlBName: string): ValidatorFn {
    return this.compareToControl(
      controlBName,
      (a: any, b: any) => isSameDay(a, b) || isAfter(a, b),
      'min',
    );
  }

  static maxControl(controlBName: string): ValidatorFn {
    return this.compareToControl(
      controlBName,
      (a: any, b: any) => isSameDay(a, b) || isBefore(a, b),
      'max',
    );
  }

  static invalidRange(controlAName: string, controlBName: string) {
    return this.compareControls(
      controlAName,
      controlBName,
      (a: any, b: any) => isSameDay(a, b) || isBefore(a, b),
      'invalidRange',
    );
  }

  static filter(
    validationFunc: (date: Date | null) => boolean,
    errKey: string,
  ): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value) {
        const controlDate = getDateUtc(control.value);
        if (isDateValid(controlDate) && !validationFunc(controlDate)) {
          return {
            [errKey]: true,
          };
        }
      }
      return null;
    };
  }

  private static compareToDate(
    dateLimit: Date,
    validateFunc: (inp: Date | null, inp2: Date | null) => boolean,
    errKey: string,
  ) {
    return (control: AbstractControl) => {
      if (control.value) {
        const controlDate = getDateUtc(control.value);
        if (isDateValid(controlDate) && !validateFunc(controlDate, dateLimit)) {
          return {
            [errKey]: this.resService.getResource(
              `${errKey.toLocaleUpperCase()}`,
              format(controlDate, APP_DATE_FORMAT),
            ),
          };
        }
      }
      return null;
    };
  }

  private static compareToDateFormated(
    dateLimit: Date,
    validateFunc: (inp: Date | null, inp2: Date | null) => boolean,
    errKey: string,
  ) {
    return (control: AbstractControl) => {
      if (control.value) {
        const controlDate = getDateUtc(
          formatDateTime(new Date(control.value))!,
        );
        if (isDateValid(controlDate) && !validateFunc(controlDate, dateLimit)) {
          return {
            [errKey]: this.resService.getResource(
              `${errKey.toLocaleUpperCase()}`,
              format(controlDate, APP_DATE_FORMAT),
            ),
          };
        }
      }
      return null;
    };
  }

  private static compareControls(
    controlAName: string,
    controlBName: string,
    validateFunc: (inp: Date | null, inp2: Date | null) => boolean,
    errKey: string,
  ): ValidatorFn {
    return (group: AbstractControl) => {
      const controlA = group.get(controlAName);
      const controlB = group.get(controlBName);
      if (!controlA) {
        throw new Error(`No '${controlAName}' control was found in group`);
      }
      if (!controlB) {
        throw new Error(`No '${controlBName}' control was found in group`);
      }

      const controlADate = getDateUtc(controlA.value);
      const controlBDate = getDateUtc(controlB.value);

      if (
        isDateValid(controlADate) &&
        isDateValid(controlBDate) &&
        !validateFunc(controlADate, controlBDate)
      ) {
        return {
          [errKey]: this.resService.getResource(
            `${errKey.toLocaleUpperCase()}`,
            format(controlBDate, APP_DATE_FORMAT),
          ),
        };
      }

      return null;
    };
  }

  private static compareToControl(
    controlBName: string,
    validateFunc: (inp: Date, inp2: Date) => boolean,
    errKey: string,
  ): ValidatorFn {
    return (controlA: AbstractControl | any) => {
      if (controlA.value) {
        const controlB = controlA.parent.get(controlBName);
        if (!controlB) {
          throw new Error(
            `No '${controlBName}' control was found in controlA.parent`,
          );
        }

        const controlBDate: any = getDateUtc(controlB.value);
        const controlADate: any = getDateUtc(controlA.value);

        if (!validateFunc(controlADate, controlBDate)) {
          return {
            [errKey]: this.resService.getResource(
              `${errKey.toLocaleUpperCase()}`,
              format(controlBDate, APP_DATE_FORMAT),
            ),
          };
        }
      }
      return null;
    };
  }
}
